export interface PointBoosterLevelValue {
    member_level_id?: number | null
    level?: number | null
    name?: string
    multiplier?: number | string | null
    origin_multiplier?: number | string | null
    is_svip?: boolean
}

export interface PointDeductionFormValue {
    deduction_enable: boolean
    deduction_frequency: number
    deduction_weekdays?: number[]
    deduction_monthdays?: number[]
    money?: number | string | null
    origin_money?: number | string | null
    high_precision_money?: number | string | null
    percentage_ceiling?: number | string | null
    mall_percentage_ceiling?: number | string | null
    base_amount?: number | string | null
    is_default_used: boolean
}

export interface PointRulesFormValue {
    point_enable: boolean
    redemption_ratio?: number | string | null
    mall_redemption_ratio?: number | string | null
    rules_list?: number[]
    redemption_range_list?: number[]
    expiration?: number
}

export interface PointSettingFormValue extends PointRulesFormValue, PointDeductionFormValue {
    levels?: PointBoosterLevelValue[]
}

const hasValue = (value: any) => value !== '' && value !== undefined && value !== null

const numberOrUndefined = (value: any) => {
    if (!hasValue(value)) return undefined

    return Number(value)
}

const numberOrNull = (value: any) => {
    if (!hasValue(value)) return null

    return Number(value)
}

const getLevelName = (level: PointBoosterLevelValue) => level.name || '该等级'

const isSvipLevel = (level: PointBoosterLevelValue) =>
    level.is_svip || (level.member_level_id == null && level.level == null)

const getLevelOrder = (level: PointBoosterLevelValue) =>
    isSvipLevel(level) ? Number.MAX_SAFE_INTEGER : Number(level.level ?? 0)

export const getPointBoosterSyncOrder = (levels: PointBoosterLevelValue[] = []) => {
    const changed = levels.filter(level => numberOrNull(level.multiplier) !== numberOrNull(level.origin_multiplier))
    const decreasing = changed
        .filter(
            level =>
                !hasValue(level.multiplier) ||
                (hasValue(level.origin_multiplier) && Number(level.multiplier) < Number(level.origin_multiplier)),
        )
        .sort((a, b) => getLevelOrder(a) - getLevelOrder(b))
    const increasing = changed
        .filter(level => !decreasing.includes(level))
        .sort((a, b) => getLevelOrder(b) - getLevelOrder(a))

    return [...decreasing, ...increasing]
}

export const validatePointDeductionValues = (values: PointDeductionFormValue) => {
    if (!values.deduction_enable) return ''
    if (!hasValue(values.money) || !hasValue(values.base_amount)) return '请填写积分抵现比例和抵扣基数'

    const money = Number(values.money)
    if (Number.isNaN(money) || money <= 0 || money > 100) return '积分抵现金额必须大于0且不超过100'

    return ''
}

export const validatePointBoosterLevels = (levels: PointBoosterLevelValue[]) => {
    let previousOrdinaryLevel: PointBoosterLevelValue | undefined
    let previousOrdinaryMultiplier: number | undefined
    let hasBlankOrdinaryAfterConfigured = false

    for (const level of [...levels].sort((a, b) => getLevelOrder(a) - getLevelOrder(b))) {
        if (!hasValue(level.multiplier)) {
            if (!isSvipLevel(level) && previousOrdinaryLevel) {
                if (hasValue(level.origin_multiplier)) {
                    return `${getLevelName(level)}积分倍数需保持与${getLevelName(previousOrdinaryLevel)}积分倍数连续`
                }
                hasBlankOrdinaryAfterConfigured = true
            }
            continue
        }

        const multiplier = Number(level.multiplier)
        if (Number.isNaN(multiplier) || multiplier < 1) return '积分倍数不能小于1'
        if (isSvipLevel(level)) {
            if (
                previousOrdinaryLevel &&
                previousOrdinaryMultiplier !== undefined &&
                multiplier < previousOrdinaryMultiplier
            ) {
                return `${getLevelName(level)}积分倍数不能小于${getLevelName(previousOrdinaryLevel)}积分倍数`
            }
            continue
        }

        if (hasBlankOrdinaryAfterConfigured && previousOrdinaryLevel) {
            return `${getLevelName(level)}积分倍数需保持与${getLevelName(previousOrdinaryLevel)}积分倍数连续`
        }

        if (
            previousOrdinaryLevel &&
            previousOrdinaryMultiplier !== undefined &&
            multiplier < previousOrdinaryMultiplier
        ) {
            return `${getLevelName(level)}积分倍数不能小于${getLevelName(previousOrdinaryLevel)}积分倍数`
        }

        previousOrdinaryLevel = level
        previousOrdinaryMultiplier = multiplier
    }

    return ''
}

export const buildPointSettingPayload = (values: PointSettingFormValue) => ({
    point_enable: values.point_enable,
    redemption_ratio: numberOrUndefined(values.redemption_ratio),
    mall_redemption_ratio: numberOrUndefined(values.mall_redemption_ratio),
    rules_list: values.rules_list || [],
    redemption_range_list: values.redemption_range_list || [],
    expiration: values.expiration,
    expiration_deal: 0,
    deduction_enable: values.deduction_enable,
    money: numberOrUndefined(values.money),
    high_precision_money:
        numberOrUndefined(values.money) === numberOrUndefined(values.origin_money)
            ? numberOrUndefined(values.high_precision_money)
            : numberOrUndefined(values.money),
    percentage_ceiling: numberOrUndefined(values.percentage_ceiling),
    mall_percentage_ceiling: numberOrUndefined(values.mall_percentage_ceiling),
    base_amount: numberOrUndefined(values.base_amount),
    is_default_used: values.is_default_used,
    deduction_frequency: values.deduction_frequency,
    deduction_weekdays:
        values.deduction_enable && values.deduction_frequency === 0 ? values.deduction_weekdays || [] : null,
    deduction_monthdays:
        values.deduction_enable && values.deduction_frequency === 1 ? values.deduction_monthdays || [] : null,
    levels: (values.levels || []).map(level => ({
        member_level_id: level.member_level_id ?? null,
        level: level.level ?? null,
        multiplier: numberOrNull(level.multiplier),
    })),
})
